/* tslint:disable:no-console max-line-length */
import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {Activity} from '@docma-mm-core/activity/activity';
import {ActivityDbService} from '@docma-mm-core/activity/db/activity-db.service';
import {MultiActivity} from '@docma-mm-core/activity/multi-activity';
import {Attachment} from '@docma-mm-core/attachment/attachment';
import {
  AttachmentFileDbEntity,
  FileDbEntity,
  mapAttachmentSourceToEntity,
  mapGroupAttachmentSourceToEntity
} from '@docma-mm-core/attachment/attachment-db.entity';
import {AttachmentDbService} from '@docma-mm-core/attachment/attachment-db.service';
import {Group} from '@docma-mm-core/group/group';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {MmPatchOperation} from '@docma-mm-core/patch/mm-patch-operation';
import {SyncFileService} from '@docma-mm-core/sync/sync-file.service';
import {Fault} from './fault';
import {FaultPersistenceService} from './fault-persistence.service';
import {FaultOperationPathEnum} from './patch/fault-operation-path.enum';
import {FaultPatch} from './patch/fault-patch';
import {FaultPatchService} from './patch/fault-patch-service';
import {GroupDbService} from '@docma-mm-core/group/group-db.service';
import {FaultsEdit} from '@docma-mm-core/fault/faults-edit';
import {Guid} from 'guid-typescript';
import {ActivityEdit} from '@docma-mm-core/activity/activity-edit';
import {AddOperation, ReplaceOperation} from 'fast-json-patch/commonjs/core';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {Subject} from 'rxjs';
import {ActivityPatchModeEnum} from '@docma-mm-core/activity/activity-patch-mode.enum';
import {CurrentFaultsSyncStateService} from '../../mm-faults/current-faults-sync-state.service';
import {Scribble} from '@docma-mm-core/scribble/scribble';
import {ScribbleDbService} from '@docma-mm-core/scribble/scribble-db.service';

/** Helper type for collecting patches during commit operations */
interface PatchCollector {
  patches: FaultPatch[];
  changedScribbleAttachments: string[];
}

/** Unchanged change state constant to avoid repeated instantiation */
const UNCHANGED_STATE = new ChangeState({ status: ChangeStatusEnum.NotChanged });

@Injectable()
export class FaultChangesCommitService implements OnDestroy {

  private activityEditCommittedSubject: Subject<{mydocmaUid: string, activityEdit: ActivityEdit}> = new Subject<{mydocmaUid: string, activityEdit: ActivityEdit}>();
  activityEditCommitted$ = this.activityEditCommittedSubject.asObservable();

  private scribblesChangedSubject = new EventEmitter<{attachments: string[]}>();
  public scribblesChangedSubject$ = this.scribblesChangedSubject.asObservable();

  constructor(private readonly patchService: FaultPatchService,
              private readonly syncFileService: SyncFileService,
              private readonly activityDbService: ActivityDbService,
              private readonly attachmentDbService: AttachmentDbService,
              private readonly scribbleDbService: ScribbleDbService,
              private readonly groupDbService: GroupDbService,
              private readonly persistenceService: FaultPersistenceService,
              private readonly currentFaultsSyncStateService: CurrentFaultsSyncStateService) {
  }

  ngOnDestroy(): void {
  }

  // ============================================================================
  // PUBLIC API - Group Changes
  // ============================================================================

  public async commitGroupChanges(mydocmaUid: string, group?: Group, multiActivity?: MultiActivity): Promise<FaultPatch[]> {
    const collector = this.createCollector();

    if (group) {
      this.addPatches(collector, await this.commitGroupChangesInternal(mydocmaUid, group));
    }

    console.info('### committing activities to index db ###');
    this.addPatches(collector, await this.commitMultiActivityChanges(mydocmaUid, group, multiActivity));
    console.info('### finished committing activities to index db ###');

    // Commit attachments if present
    const initActivity = multiActivity.entities?.[0]?.Activity;
    const changedAttachments = initActivity?.attachments?.filter(att => this.hasChanges(att));

    if (changedAttachments?.length > 0) {
      console.info('### committing attachments to index db ###');
      this.addPatches(collector, await this.commitMultiActivityAttachmentsChanges(mydocmaUid, group, changedAttachments, multiActivity));
      console.info('### finished committing attachments to index db ###');
    }

    await this.currentFaultsSyncStateService.set(mydocmaUid);
    return collector.patches;
  }

  // ============================================================================
  // PUBLIC API - Fault Changes
  // ============================================================================

  public async commitFaultChanges(
    mydocmaUid: string,
    fault: Fault,
    commitGroup: boolean,
    group?: Group,
    multiActivity?: Activity
  ): Promise<FaultPatch[]> {
    const collector = this.createCollector();

    // Commit fault changes
    if (this.hasChanges(fault)) {
      await this.commitFaultDataChanges(mydocmaUid, fault, collector);
    }

    // Commit fault attachments and their scribbles
    await this.commitFaultAttachmentsWithScribbles(mydocmaUid, fault, collector);

    // Commit activities
    await Promise.all(fault.activities.map(ac =>
      this.commitActivityChanges(mydocmaUid, fault, ac).then(p => this.addPatches(collector, p))
    ));

    // Emit scribble changes if any
    this.emitScribbleChanges(collector);

    await this.currentFaultsSyncStateService.set(mydocmaUid);
    return collector.patches;
  }

  // ============================================================================
  // PUBLIC API - Faults Edit Changes (Batch)
  // ============================================================================

  public async commitFaultsEditChanges(mydocmaUid: string, faultsEdit: FaultsEdit): Promise<FaultPatch[]> {
    const patches: FaultPatch[] = [];
    const faultsToDiscard: Fault[] = [];
    const gUid = Guid.create().toString();

    for (const fault of faultsEdit.loadedFauls) {
      const viewFault = faultsEdit.faults.get(fault.Id);
      viewFault.changeState.data = faultsEdit.changes;

      if (this.hasChanges(viewFault)) {
        const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.faults, viewFault.changeState);
        patches.push({
          fUid: viewFault.uid,
          gUid,
          gId: null,
          oId: viewFault.id,
          oUid: viewFault.uid,
          date: viewFault.changeState.date,
          operation: op
        });
        viewFault.syncState = this.addChangesToSyncState(viewFault.syncState, viewFault.changeState);
        faultsToDiscard.push(viewFault);
      }
    }

    const savedPatches = await this.patchService.saveAll(mydocmaUid, patches);
    await this.discardFaultsChanges(mydocmaUid, faultsToDiscard);
    await this.currentFaultsSyncStateService.set(mydocmaUid);

    return savedPatches;
  }

  // ============================================================================
  // PUBLIC API - Activity Changes
  // ============================================================================

  public async commitActivityChanges(mydocmaUid: string, fault: Fault, activity: Activity, commitGroup?: boolean): Promise<FaultPatch[]> {
    const collector = this.createCollector();

    if (this.hasChanges(activity)) {
      const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.activity, activity.changeState);
      const savedPatches = await this.patchService.save(mydocmaUid, [{
        fUid: fault.uid,
        oId: activity.id,
        oUid: activity.uid,
        pId: fault.id,
        date: activity.changeState.date,
        operation: op
      }]);
      this.addPatches(collector, savedPatches);
      activity.syncState = this.addChangesToSyncState(activity.syncState, activity.changeState);
      await this.discardActivityChanges(mydocmaUid, fault.uid, activity);
    }

    // Commit activity attachments with scribbles
    await this.commitActivityAttachmentsWithScribbles(mydocmaUid, fault, activity, collector);
    this.emitScribbleChanges(collector);

    return collector.patches;
  }

  // ============================================================================
  // PUBLIC API - Scribble Changes
  // ============================================================================

  public async commitScribbleChanges(
    mydocmaUid: string,
    fault: Fault,
    activity: Activity,
    attachment: Attachment,
    scribble: Scribble,
    operation: FaultOperationPathEnum
  ): Promise<FaultPatch[]> {
    if (!this.hasChanges(scribble)) {
      return [];
    }

    const op = this.patchService.generateOperationFromChangeState(operation, scribble.changeState);
    const savedPatches = await this.patchService.save(mydocmaUid, [{
      fUid: fault?.uid ?? null,
      pId: attachment.id,
      pUid: attachment.uid,
      oId: scribble.id,
      oUid: scribble.uid,
      gId: scribble.data?.ScribbleLayer?.Id,
      gUid: scribble.data?.ScribbleLayer?.UniqueId,
      aUid: activity?.uid,
      date: scribble.changeState.date,
      operation: op
    }]);

    scribble.syncState = this.addChangesToSyncState(scribble.syncState, scribble.changeState);
    await this.discardScribbleChanges(mydocmaUid, fault.uid, activity?.uid, attachment, scribble);

    return savedPatches;
  }

  // ============================================================================
  // PUBLIC API - Attachment Changes
  // ============================================================================

  public async commitAttachmentChanges(
    mydocmaUid: string,
    fault: Fault,
    activity: Activity,
    group: Group,
    attachment: Attachment,
    multiActivity?: MultiActivity
  ): Promise<FaultPatch[]> {
    if (!this.hasChanges(attachment)) {
      return [];
    }

    const { op, pId, pUid, gId, gUid } = this.getAttachmentPatchParams(fault, activity, group, attachment);

    const savedPatches = await this.patchService.save(mydocmaUid, [{
      fUid: fault?.uid ?? null,
      pId,
      pUid,
      oId: attachment.id,
      oUid: attachment.uid,
      gId,
      gUid,
      date: attachment.changeState.date,
      operation: op
    }]);

    if (op.op === 'add') {
      await this.saveAttachmentFile(mydocmaUid, fault, group, attachment);
    }

    attachment.syncState = this.addChangesToSyncState(attachment.syncState, attachment.changeState);

    if (fault) {
      await this.discardAttachmentChanges(mydocmaUid, fault.uid, activity?.uid, attachment);
    } else if (group) {
      await this.discardGroupAttachmentChanges(mydocmaUid, group, activity?.uid, attachment, multiActivity);
    }

    return savedPatches;
  }

  public async commitMultiActivityAttachmentsChanges(
    mydocmaUid: string,
    group: Group,
    attachments: Attachment[],
    multiActivity?: MultiActivity
  ): Promise<FaultPatch[]> {
    const patches: FaultPatch[] = [];
    const newFiles: FileDbEntity[] = [];

    for (const attachment of attachments) {
      const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.groupAttachment, attachment.changeState);

      patches.push({
        pId: group.id,
        pUid: group.uid,
        oId: attachment.id,
        oUid: attachment.uid,
        gId: group.id,
        gUid: group.uid,
        date: attachment.changeState.date,
        operation: op
      });

      if (op.op === 'add' && group) {
        const fileEntity = await this.createGroupAttachmentFileEntity(group, attachment);
        if (fileEntity) {
          newFiles.push(fileEntity);
        }
      }
    }

    const savedPatches = await this.patchService.save(mydocmaUid, patches);

    // Save all files in parallel
    await Promise.all(newFiles.map(file => this.syncFileService.saveFile(mydocmaUid, file)));

    // Update sync states
    for (const attachment of attachments) {
      attachment.syncState = this.addChangesToSyncState(attachment.syncState, attachment.changeState);
    }

    if (group) {
      await this.discardGroupAttachmentsChanges(mydocmaUid, group, attachments, multiActivity);
    }

    return savedPatches;
  }

  // ============================================================================
  // DISCARD METHODS
  // ============================================================================

  public async discardFaultChanges(mydocmaUid: string, fault: Fault, deepDiscard: boolean = true): Promise<void> {
    if (!this.hasChanges(fault)) {
      return;
    }
    fault.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
    await this.persistenceService.saveChangeState(mydocmaUid, fault.uid, fault.changeState);
  }

  public async discardFaultsChanges(mydocmaUid: string, faults: Fault[], deepDiscard: boolean = true): Promise<void> {
    const discardItems = faults
      .filter(f => this.hasChanges(f))
      .map(f => {
        f.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
        return f.uid;
      });

    if (discardItems.length > 0) {
      await this.persistenceService.saveChangeStates(mydocmaUid, discardItems, UNCHANGED_STATE);
    }
  }

  public async discardAttachmentChanges(mydocmaUid: string, faultUid: string, activityUid: string, attachment: Attachment): Promise<void> {
    if (!this.hasChanges(attachment)) {
      return;
    }
    attachment.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
    await this.attachmentDbService.saveChangeState(mydocmaUid, faultUid, activityUid, null, attachment.uid, attachment.changeState);
  }

  public async discardGroupAttachmentChanges(
    mydocmaUid: string,
    group: Group,
    activityUid: string,
    attachment: Attachment,
    multiActivity: MultiActivity
  ): Promise<void> {
    if (!this.hasChanges(attachment)) {
      return;
    }
    attachment.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });

    // Save in parallel for all entities
    await Promise.all(multiActivity.entities.map(entity =>
      this.attachmentDbService.saveChangeState(
        mydocmaUid,
        entity.Fault.uid,
        entity.Activity.uid,
        group?.uid,
        attachment.uid,
        attachment.changeState
      )
    ));
  }

  public async discardGroupAttachmentsChanges(
    mydocmaUid: string,
    group: Group,
    attachments: Attachment[],
    multiActivity: MultiActivity
  ): Promise<void> {
    for (const attachment of attachments) {
      attachment.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
    }
    await this.attachmentDbService.saveChangeStateMultiActivity(mydocmaUid, attachments, multiActivity);
  }

  public async discardScribbleChanges(
    mydocmaUid: string,
    faultUid: string,
    activityUid: string,
    attachment: Attachment,
    scribble: Scribble
  ): Promise<void> {
    if (!this.hasChanges(scribble)) {
      return;
    }
    scribble.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
    await this.scribbleDbService.saveChangeState(mydocmaUid, faultUid, activityUid, null, attachment.uid, scribble.uid, scribble.changeState);
  }

  public async discardActivityChanges(mydocmaUid: string, faultUid: string, activity: Activity): Promise<void> {
    if (!this.hasChanges(activity)) {
      return;
    }
    activity.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
    await this.activityDbService.saveChangeState(mydocmaUid, faultUid, activity.uid, activity.changeState);
  }

  public async discardActivitiesChanges(mydocmaUid: string, activities: Activity[]): Promise<void> {
    const discardItems = activities
      .filter(a => this.hasChanges(a))
      .map(a => {
        a.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
        return { activityUid: a.uid, faultUid: a.fault.uid };
      });

    if (discardItems.length > 0) {
      await this.activityDbService.saveChangeStates(mydocmaUid, discardItems, UNCHANGED_STATE);
    }
  }

  public async discardGroupChanges(mydocmaUid: string, group: Group): Promise<void> {
    if (!this.hasChanges(group)) {
      return;
    }
    group.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
    await this.groupDbService.saveChangeState(mydocmaUid, group.uid, group.changeState);
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /** Creates a new patch collector */
  private createCollector(): PatchCollector {
    return { patches: [], changedScribbleAttachments: [] };
  }

  /** Adds patches to collector */
  private addPatches(collector: PatchCollector, patches: FaultPatch[]): void {
    if (patches?.length > 0) {
      collector.patches.push(...patches);
    }
  }

  /** Checks if an entity has changes */
  private hasChanges(entity: { changeState: ChangeState<any> }): boolean {
    return entity?.changeState?.status !== ChangeStatusEnum.NotChanged;
  }

  /** Emits scribble changes event if any attachments were changed */
  private emitScribbleChanges(collector: PatchCollector): void {
    if (collector.changedScribbleAttachments.length > 0) {
      this.scribblesChangedSubject.emit({ attachments: collector.changedScribbleAttachments });
    }
  }

  /** Creates sync state with added changes */
  private addChangesToSyncState(syncState: SyncState, changeState: ChangeState<any>): SyncState {
    const newSyncState = new SyncState(syncState);
    newSyncState.changes = [...(syncState.changes ?? []), new ChangeState(changeState)];
    return newSyncState;
  }

  /** Gets attachment patch parameters based on context */
  private getAttachmentPatchParams(
    fault: Fault,
    activity: Activity,
    group: Group,
    attachment: Attachment
  ): { op: MmPatchOperation; pId: number; pUid: string; gId?: number; gUid?: string } {
    if (fault && !activity) {
      return {
        op: this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.attachment, attachment.changeState),
        pId: fault.id,
        pUid: fault.uid
      };
    } else if (group) {
      return {
        op: this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.groupAttachment, attachment.changeState),
        pId: group.id,
        pUid: group.uid,
        gId: group.id,
        gUid: group.uid
      };
    } else if (fault && activity) {
      return {
        op: this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.activityAttachment, attachment.changeState),
        pId: activity.id,
        pUid: activity.uid
      };
    }
    return { op: null, pId: null, pUid: null };
  }

  /** Saves attachment file entity */
  private async saveAttachmentFile(mydocmaUid: string, fault: Fault, group: Group, attachment: Attachment): Promise<void> {
    let attachmentFileEntity: AttachmentFileDbEntity;

    if (fault) {
      attachmentFileEntity = await mapAttachmentSourceToEntity(fault.uid, null, attachment);
    } else if (group) {
      attachmentFileEntity = await mapGroupAttachmentSourceToEntity(group, attachment);
    }

    if (attachmentFileEntity) {
      const fileEntity = attachmentFileEntity.loadState.data;
      fileEntity.uid = attachment.uid;
      await this.syncFileService.saveFile(mydocmaUid, fileEntity);
    }
  }

  /** Creates file entity for group attachment */
  private async createGroupAttachmentFileEntity(group: Group, attachment: Attachment): Promise<FileDbEntity | null> {
    const attachmentFileEntity = await mapGroupAttachmentSourceToEntity(group, attachment);
    if (attachmentFileEntity) {
      const fileEntity = attachmentFileEntity.loadState.data;
      fileEntity.uid = attachment.uid;
      return fileEntity;
    }
    return null;
  }

  /** Commits fault data changes with error handling for partial retries */
  private async commitFaultDataChanges(mydocmaUid: string, fault: Fault, collector: PatchCollector): Promise<void> {
    if (fault.syncState.error) {
      await this.commitFaultDataWithErrorHandling(mydocmaUid, fault, collector);
    } else {
      const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.fault, fault.changeState);
      const savedPatches = await this.patchService.save(mydocmaUid, [{
        fUid: fault.uid,
        oId: fault.id,
        date: fault.changeState.date,
        operation: op
      }]);
      this.addPatches(collector, savedPatches);
    }

    fault.syncState = this.addChangesToSyncState(fault.syncState, fault.changeState);
    await this.discardFaultChanges(mydocmaUid, fault);
  }

  /** Handles fault data commit when there was a previous sync error */
  private async commitFaultDataWithErrorHandling(mydocmaUid: string, fault: Fault, collector: PatchCollector): Promise<void> {
    let errorFields: string[] = [];

    try {
      if (fault.syncState.error instanceof Object) {
        errorFields = (fault.syncState.error?.InternalErrors?.length ?? 0) > 0
          ? fault.syncState.error.InternalErrors.map(x => x.Prop)
          : JSON.parse(fault.syncState.error).InternalErrors.map(x => x.Prop);
      }
    } catch {
      errorFields = [];
    }

    const existingPatches = await this.patchService.getByFaultUid(mydocmaUid, fault.uid);
    const f1 = existingPatches.findIndex(f => f.gUid != null);

    const patchTimes = existingPatches
      .slice(0, f1 === -1 ? existingPatches.length : f1)
      .filter(x => !x.gUid && (fault.changeState.status === ChangeStatusEnum.New || fault.changeState.status === ChangeStatusEnum.Changed))
      .map(o => o.date.getTime());

    const data1 = new FaultData();
    const data2 = new FaultData();

    for (const key in fault.changeState.data) {
      if (errorFields.includes(key)) {
        data1[key] = fault.changeState.data[key];
      } else {
        data2[key] = fault.changeState.data[key];
      }
    }

    const updateDate = new Date(Math.max(...patchTimes) + 1000);

    const changeState1 = new ChangeState({ status: fault.changeState.status, date: fault.changeState.date, data: data1 });
    const changeState2 = new ChangeState({ status: fault.changeState.status, date: fault.changeState.date, data: data2 });

    const op1 = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.fault, changeState1);
    const op2 = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.fault, changeState2);

    const [savedPatches1, savedPatches2] = await Promise.all([
      this.patchService.save(mydocmaUid, [{ fUid: fault.uid, oId: fault.id, date: updateDate, operation: op1 }]),
      this.patchService.save(mydocmaUid, [{ fUid: fault.uid, oId: fault.id, date: changeState2.date, operation: op2 }])
    ]);

    this.addPatches(collector, savedPatches1);
    this.addPatches(collector, savedPatches2);
  }

  /** Commits fault attachments and their scribbles */
  private async commitFaultAttachmentsWithScribbles(mydocmaUid: string, fault: Fault, collector: PatchCollector): Promise<void> {
    for (const attachment of fault.attachments) {
      const attachmentPatches = await this.commitAttachmentChanges(mydocmaUid, fault, null, null, attachment);
      this.addPatches(collector, attachmentPatches);

      for (const scribble of attachment.scribbles) {
        const scribblePatches = await this.commitScribbleChanges(
          mydocmaUid, fault, undefined, attachment, scribble, FaultOperationPathEnum.attachmentScribbles
        );
        this.addPatches(collector, scribblePatches);

        if (scribblePatches.length > 0 && scribblePatches[0].oUid === scribble.uid) {
          collector.changedScribbleAttachments.push(scribblePatches[0].pUid);
        }
      }
    }
  }

  /** Commits activity attachments and their scribbles */
  private async commitActivityAttachmentsWithScribbles(
    mydocmaUid: string,
    fault: Fault,
    activity: Activity,
    collector: PatchCollector
  ): Promise<void> {
    for (const attachment of activity.attachments) {
      const attachmentPatches = await this.commitAttachmentChanges(mydocmaUid, fault, activity, null, attachment);
      this.addPatches(collector, attachmentPatches);

      for (const scribble of attachment.scribbles) {
        const scribblePatches = await this.commitScribbleChanges(
          mydocmaUid, fault, activity, attachment, scribble, FaultOperationPathEnum.activityAttachmentScribbles
        );
        this.addPatches(collector, scribblePatches);

        if (scribblePatches.length > 0 && scribblePatches[0].oUid === scribble.uid) {
          collector.changedScribbleAttachments.push(scribblePatches[0].pUid);
        }
      }
    }
  }

  /** Commits group changes internal */
  private async commitGroupChangesInternal(mydocmaUid: string, group: Group): Promise<FaultPatch[]> {
    if (!this.hasChanges(group)) {
      return [];
    }

    const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.group, group.changeState);
    const savedPatches = await this.patchService.save(mydocmaUid, [{
      gUid: group.uid,
      gId: group.id,
      oId: group.id,
      oUid: group.uid,
      date: group.changeState.date,
      operation: op
    }]);

    group.syncState = this.addChangesToSyncState(group.syncState, group.changeState);
    await this.discardGroupChanges(mydocmaUid, group);

    return savedPatches;
  }

  /** Commits multi-activity changes */
  private async commitMultiActivityChanges(mydocmaUid: string, group: Group, multiActivity?: MultiActivity): Promise<FaultPatch[]> {
    const patches: FaultPatch[] = [];
    const activities: Activity[] = [];

    const entities = multiActivity.patchMode === ActivityPatchModeEnum.MultiActivity_Single
      ? multiActivity.entities.filter(e => e.IsInitActivity)
      : multiActivity.entities;

    const options: { [optionKey: string]: any } = {
      ActivityPatchModeEnum: multiActivity.patchMode
    };

    for (const entity of entities) {
      if (this.hasChanges(entity.Activity)) {
        const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.groupActivity, entity.Activity.changeState);
        patches.push({
          fUid: entity.Fault.uid,
          gUid: group.uid,
          gId: group.id,
          oId: entity.Activity.id,
          oUid: entity.Activity.uid,
          date: entity.Activity.changeState.date,
          operation: op,
          options
        });
        entity.Activity.syncState = this.addChangesToSyncState(entity.Activity.syncState, entity.Activity.changeState);
        activities.push(entity.Activity);
      }
    }

    const savedPatches = await this.patchService.saveAll(mydocmaUid, patches);
    await this.discardActivitiesChanges(mydocmaUid, activities);

    if (options.ActivityPatchModeEnum && savedPatches.length > 0) {
      const activityEdit = new ActivityEdit();
      activityEdit.activities = activities;
      activityEdit.changes = savedPatches[0].operation.value;
      this.activityEditCommittedSubject.next({ mydocmaUid, activityEdit });
    }

    await this.currentFaultsSyncStateService.set(mydocmaUid);
    return savedPatches;
  }
}
